import React, { useState, useEffect } from 'react';
import CommonModalForm from '../../components/commonComponent/CommonModalForm';
import { validateForm } from '../../utils/formUtils';
import { toastSuccess, toastError } from '../../utils/alertHelper';
import axios from '../../utils/axiosInstance';
import { FaEdit, FaTrash } from 'react-icons/fa';

const SmallBannerPage = () => {
  const [banners, setBanners] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [formValues, setFormValues] = useState({
    images: null,
    link: '',
    active: false,
    startDate: '',
    endDate: '',
    price: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false); // Add filter modal state
  const [tempDateRange, setTempDateRange] = useState({ start: '', end: '' }); // Temp for modal

  // Date range and stats
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [filteredStats, setFilteredStats] = useState({ count: 0, totalPrice: 0 });

  const limit = 10000; // Set a very high limit to fetch all records

  useEffect(() => {
    fetchBanners(1, searchQuery); // Always fetch all on search change
    // eslint-disable-next-line
  }, [searchQuery]);

  useEffect(() => {
    computeStats(banners, dateRange);
    // eslint-disable-next-line
  }, [banners, dateRange]);

  const fetchBanners = async (page = 1, search = '') => {
    try {
      setLoading(true);
      let url = `/admin/api/smallbanners?page=1&limit=${limit}&search=${search}`;
      const res = await axios.get(url);
      let bannersData = [];
      let totalCount = 0;
      if (res.data.smallbanners) {
        bannersData = res.data.smallbanners;
        totalCount = res.data.totalCount || res.data.smallbanners.length;
      } else if (res.data.smallbanner) {
        bannersData = [res.data.smallbanner];
        totalCount = 1;
      } else if (Array.isArray(res.data)) {
        bannersData = res.data;
        totalCount = res.data.length;
      } else {
        bannersData = [];
        totalCount = 0;
      }
      setBanners(bannersData);
      setTotalPages(1); // Only one page, all records
      computeStats(bannersData, dateRange);
    } catch (err) {
      toastError('Failed to fetch small banners');
    } finally {
      setLoading(false);
    }
  };

  const computeStats = (bannerList = banners, range = dateRange) => {
    let filtered = bannerList;
    if (range.start && range.end) {
      const start = new Date(range.start);
      const end = new Date(range.end);
      filtered = bannerList.filter(b =>
        b.startDate && b.endDate &&
        new Date(b.startDate) >= start &&
        new Date(b.endDate) <= end
      );
    }
    const count = filtered.length;
    const totalPrice = filtered.reduce((sum, b) =>
      sum + (Number(b.price) || 0), 0
    );
    setFilteredStats({ count, totalPrice });
  };

  const handleSave = async () => {
    const validationErrors = validateForm(formValues, {
      images: { required: !editingId },
      link: { required: true },
      startDate: { required: true },
      endDate: { required: true },
      price: { required: true },
    });

    // File validation
    if (formValues.images instanceof File) {
      const maxSize = 5 * 1024 * 1024;
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (formValues.images.size > maxSize) {
        validationErrors.images = 'Image size should be less than 5MB';
      }
      if (!allowedTypes.includes(formValues.images.type)) {
        validationErrors.images = 'Only JPEG, PNG, GIF, and WebP images are allowed';
      }
    }

    // Date validation
    if (formValues.startDate && formValues.endDate) {
      const startDate = new Date(formValues.startDate);
      const endDate = new Date(formValues.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        validationErrors.startDate = 'Start date cannot be in the past';
      }
      if (endDate <= startDate) {
        validationErrors.endDate = 'End date must be after start date';
      }
    }

    // Price validation
    if (formValues.price && isNaN(Number(formValues.price))) {
      validationErrors.price = 'Price must be a number';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setSaving(true);
      const formData = new FormData();
      if (formValues.images instanceof File) {
        formData.append('images', formValues.images);
      }
      formData.append('active', formValues.active);
      formData.append('link', formValues.link || '');
      formData.append('startDate', formValues.startDate || '');
      formData.append('endDate', formValues.endDate || '');
      formData.append('price', formValues.price || '');

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      let response;
      if (editingId) {
        response = await axios.put(`/admin/api/smallbanners/${editingId}`, formData, config);
        toastSuccess('Small Banner updated successfully!');
      } else {
        response = await axios.post('/admin/api/smallbanners', formData, config);
        toastSuccess('Small Banner added successfully!');
      }

      setCurrentPage(1);
      fetchBanners(1, searchQuery);
      handleCancel();
    } catch (err) {
      toastError(err.response?.data?.Message || 'Failed to save Chapter Ads');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (banner) => {
    setFormValues({
      images: null,
      link: banner.link || '',
      active: banner.active || false,
      startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
      endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
      price: banner.price || '',
    });
    setImagePreview(banner.images || '');
    setEditingId(banner._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this chapter ad?')) {
      try {
        await axios.delete(`/admin/api/smallbanners/${id}`);
        toastSuccess('Chapter Ads deleted successfully!');
        setCurrentPage(1);
        fetchBanners(1, searchQuery);
      } catch (err) {
        toastError('Failed to delete Chapter Ads');
      }
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setFormValues({
      images: null,
      link: '',
      active: false,
      startDate: '',
      endDate: '',
      price: '',
    });
    setImagePreview('');
    setEditingId(null);
    setErrors({});
  };

  const handleImageChange = (name, file) => {
    setFormValues(prevValues => ({ ...prevValues, [name]: file }));
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview('');
    }
  };

  const handleViewBanner = (banner) => {
    setSelectedBanner(banner);
    setShowViewModal(true);
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const formData = new FormData();
      formData.append('active', !currentStatus);

      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      await axios.put(`/admin/api/smallbanners/${id}`, formData, config);

      toastSuccess(`Chapter Ads ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchBanners(currentPage, searchQuery);
    } catch (err) {
      toastError(err.response?.data?.Message || 'Failed to update chapter ads status');
    }
  };

  // Date Range Filter Handlers
  const handleDateFilter = () => {
    setTempDateRange(dateRange); // Open modal with current range
    setShowFilterModal(true);
  };
  const handleApplyFilter = () => {
    setDateRange(tempDateRange);
    computeStats(banners, tempDateRange);
    setShowFilterModal(false);
  };
  const handleDateReset = () => {
    setDateRange({ start: '', end: '' });
    setTempDateRange({ start: '', end: '' });
    computeStats(banners, { start: '', end: '' });
    setShowFilterModal(false);
  };

  return (
    <div className="container mt-2">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0"> Chapter Ads</h3>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Search Chapter Ads..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Stats and Filter Row */}
      <div className="d-flex justify-content-between align-items-end mb-3 flex-wrap">
        {/* Stats Section */}
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <div>
            <span className="fw-bold">Banners count:</span>
            <span className="ms-1">{filteredStats.count}</span>
          </div>
          <div>
            <span className="fw-bold">Total price:</span>
            <span className="ms-1">₹{filteredStats.totalPrice}</span>
          </div>
          {dateRange.start && dateRange.end && (
            <span className="text-muted ms-2" style={{ fontSize: '13px' }}>
              (filtered by date range)
            </span>
          )}
        </div>
        {/* Filter Section */}
        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>Start Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.start}
              onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <div>
            <label className="form-label mb-0" style={{ fontSize: '13px' }}>End Date</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateRange.end}
              onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
              style={{ minWidth: 130 }}
            />
          </div>
          <button
            className="btn btn-sm btn-primary"
            onClick={handleDateFilter}
            style={{ minWidth: 80 }}
          >
            Filter
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={handleDateReset}
            style={{ minWidth: 80 }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-header d-flex justify-content-between align-items-center p-2">
          <h5 className="mb-0">Chapter Ads List</h5>
          <button
            className="btn btn-sm"
            style={{ backgroundColor: '#C44C23', color: '#fff' }}
            onClick={() => {
              setFormValues({
                images: null,
                link: '',
                active: false,
                startDate: '',
                endDate: '',
                price: '',
              });
              setImagePreview('');
              setEditingId(null);
              setErrors({});
              setShowModal(true);
            }}
          >
            + Add New Chapter Ads
          </button>
        </div>
        <div className="table-responsive">
          <div className="card-body p-0">
            <table
              className="table table-bordered table-sm mb-0"
              style={{ fontSize: '14px', lineHeight: '2' }}
            >
              <thead className="table-light">
                <tr>
                  <th className="py-1 px-2 w-5">Sr no.</th>
                  <th className="py-1 px-2">Ads Id</th>
                  <th className="py-1 px-2">Image</th>
                  <th className="py-1 px-2">Link</th>
                  <th className="py-1 px-2">Start Date</th>
                  <th className="py-1 px-2">End Date</th>
                  <th className="py-1 px-2">Price</th>
                  <th className="py-1 px-2">Status</th>
                  <th className="py-1 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center py-3">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : banners.length > 0 ? (
                  banners.map((banner, idx) => (
                    <tr key={banner._id}>
                      <td className="py-1 px-2">{idx + 1}</td>
                      <td className="py-1 px-2">{banner.bannerId || banner._id}</td>
                      <td className="py-1 px-2">
                        {banner.images && banner.images.trim() !== '' ? (
                          <img
                            src={banner.images}
                            alt="Chapter Ads"
                            style={{
                              width: '60px',
                              height: '40px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                            onClick={() => window.open(banner.images, '_blank')}
                            title="Click to view full image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'inline';
                            }}
                          />
                        ) : null}
                        {(!banner.images || banner.images.trim() === '') && (
                          <span className="text-muted">No Image</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.link ? (
                          <a
                            href={banner.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary text-decoration-none"
                            style={{ fontSize: '12px' }}
                            title={banner.link}
                          >
                            {banner.link.length > 30 ? banner.link.substring(0, 30) + '...' : banner.link}
                          </a>
                        ) : (
                          <span className="text-muted">No Link</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.startDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.startDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.endDate ? (
                          <span style={{ fontSize: '12px' }}>
                            {new Date(banner.endDate).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="py-1 px-2">
                        {banner.price !== undefined && banner.price !== null && banner.price !== ''
                          ? banner.price
                          : <span className="text-muted">-</span>}
                      </td>
                      <td className="py-1 px-2">
                        <span
                          className={`badge ${banner.active ? 'badge-success' : 'badge-secondary'}`}
                          style={{
                            backgroundColor: banner.active ? '#28a745' : '#6c757d',
                            color: 'white',
                            fontSize: '11px',
                            padding: '4px 8px',
                            cursor: 'pointer'
                          }}
                          onClick={() => handleToggleStatus(banner._id, banner.active)}
                          title={`Click to ${banner.active ? 'deactivate' : 'activate'}`}
                        >
                          {banner.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-1 px-2">
                        <button
                          className="btn btn-sm me-1 py-0 px-2 fs-6 lh-sm shadow-none border-0"
                          style={{ color: '#C44C23' }}
                          title="Edit"
                          onClick={() => handleEdit(banner)}
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          className="btn btn-sm py-0 px-2 fs-6 lh-sm shadow-none border-0"
                          style={{ color: 'red' }}
                          title="Delete"
                          onClick={() => handleDelete(banner._id)}
                        >
                          <FaTrash size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center py-2">No Chapter Ads found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Remove pagination controls */}
          </div>
        </div>
      </div>

      {/* Modal for add/edit */}
      {showModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Chapter Ads' : 'Add Chapter Ads'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                ></button>
              </div>
              <div className="modal-body">
                <form>
                  {/* Image Upload */}
                  <div className="mb-3">
                    <label className="form-label">Chapter Ads Image <span className="text-danger">*</span></label>
                    <input
                      type="file"
                      className={`form-control ${errors.images ? 'is-invalid' : ''}`}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setFormValues(prev => ({ ...prev, images: file }));
                          setImagePreview(URL.createObjectURL(file));
                          if (errors.images) {
                            setErrors(prev => ({ ...prev, images: '' }));
                          }
                        }
                      }}
                    />
                    {errors.images && <div className="invalid-feedback">{errors.images}</div>}
                    {imagePreview && (
                      <div className="mt-2">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                          className="img-thumbnail"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Chapter Ads Link <span className="text-danger">*</span></label>
                    <input
                      type="url"
                      className={`form-control ${errors.link ? 'is-invalid' : ''}`}
                      placeholder="Enter Chapter Ads URL (e.g., https://example.com)"
                      value={formValues.link}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, link: e.target.value }));
                        if (errors.link) {
                          setErrors(prev => ({ ...prev, link: '' }));
                        }
                      }}
                    />
                    {errors.link && <div className="invalid-feedback">{errors.link}</div>}
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Start Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                        value={formValues.startDate}
                        onChange={(e) => {
                          setFormValues(prev => ({ ...prev, startDate: e.target.value }));
                          if (errors.startDate) {
                            setErrors(prev => ({ ...prev, startDate: '' }));
                          }
                        }}
                      />
                      {errors.startDate && <div className="invalid-feedback">{errors.startDate}</div>}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">End Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                        value={formValues.endDate}
                        onChange={(e) => {
                          setFormValues(prev => ({ ...prev, endDate: e.target.value }));
                          if (errors.endDate) {
                            setErrors(prev => ({ ...prev, endDate: '' }));
                          }
                        }}
                      />
                      {errors.endDate && <div className="invalid-feedback">{errors.endDate}</div>}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Price <span className="text-danger">*</span></label>
                    <input
                      type="number"
                      className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                      placeholder="Enter price"
                      value={formValues.price}
                      onChange={(e) => {
                        setFormValues(prev => ({ ...prev, price: e.target.value }));
                        if (errors.price) {
                          setErrors(prev => ({ ...prev, price: '' }));
                        }
                      }}
                      readOnly={!!editingId}
                    />
                    {errors.price && <div className="invalid-feedback">{errors.price}</div>}
                  </div>
                  <div className="mb-3">
                    <label className="form-label d-block">Active Status</label>
                    <button
                      type="button"
                      className={`btn btn-sm ${formValues.active ? 'btn-success' : 'btn-secondary'}`}
                      style={{ minWidth: 80 }}
                      onClick={() => setFormValues(prev => ({ ...prev, active: !prev.active }))}
                    >
                      {formValues.active ? 'Active' : 'Inactive'}
                    </button>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ backgroundColor: '#C44C23', color: 'white' }}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (editingId ? 'Updating...' : 'Saving...') : (editingId ? 'Update' : 'Save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Small Banner Modal */}
      {showViewModal && selectedBanner && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Chapter Ads Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowViewModal(false)}
                ></button>
              </div>
              <div className="modal-body text-center">
                <div className="mb-3">
                  {selectedBanner.images && selectedBanner.images.trim() !== '' ? (
                    <img
                      src={selectedBanner.images}
                      alt="Chapter Ads"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '500px',
                        objectFit: 'contain'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'block';
                      }}
                    />
                  ) : (
                    <div
                      className="d-flex align-items-center justify-content-center bg-light"
                      style={{
                        width: '100%',
                        height: '300px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px'
                      }}
                    >
                      <span className="text-muted">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Status:</strong>
                      <span className={`ms-2 badge ${selectedBanner.active ? 'badge-success' : 'badge-secondary'}`}>
                        {selectedBanner.active ? 'Active' : 'Inactive'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>Created:</strong> {new Date(selectedBanner.createdAt).toLocaleDateString()}</p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Ads Id:</strong>
                      <span className="ms-2">
                        {selectedBanner.bannerId || selectedBanner._id}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <p><strong>Link:</strong>
                      {selectedBanner.link ? (
                        <a href={selectedBanner.link} target="_blank" rel="noopener noreferrer" className="ms-2 text-primary">
                          {selectedBanner.link}
                        </a>
                      ) : (
                        <span className="ms-2 text-muted">No link provided</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <p><strong>Start Date:</strong>
                      <span className="ms-2">
                        {selectedBanner.startDate ? new Date(selectedBanner.startDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                  <div className="col-md-6">
                    <p><strong>End Date:</strong>
                      <span className="ms-2">
                        {selectedBanner.endDate ? new Date(selectedBanner.endDate).toLocaleDateString() : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <p><strong>Price:</strong>
                      <span className="ms-2">
                        {selectedBanner.price !== undefined && selectedBanner.price !== null && selectedBanner.price !== ''
                          ? selectedBanner.price
                          : 'Not set'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </button>
                {selectedBanner.link && (
                  <button
                    type="button"
                    className="btn btn-success"
                    onClick={() => window.open(selectedBanner.link, '_blank')}
                  >
                    Visit Link
                  </button>
                )}
                {selectedBanner.images && selectedBanner.images.trim() !== '' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => window.open(selectedBanner.images, '_blank')}
                  >
                    Open Image
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.7)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Filter by Date Range</h5>
                <button type="button" className="btn-close" onClick={() => setShowFilterModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={tempDateRange.start}
                    onChange={e => setTempDateRange(prev => ({ ...prev, start: e.target.value }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={tempDateRange.end}
                    onChange={e => setTempDateRange(prev => ({ ...prev, end: e.target.value }))}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowFilterModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDateReset}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApplyFilter}
                  disabled={!tempDateRange.start || !tempDateRange.end}
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmallBannerPage;